import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  CircularProgress,
  Button,
  Alert,
  Card,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import ShieldIcon from '@mui/icons-material/Shield';

import { trackLogin } from '../../../utils/gtm/GTMService';
import { tokenStorage } from '../../../utils/tokenStorage';
import { loginWithRfx } from '../../../features/register/services/post/login/login';
import { verifyTwoFactorLogin, sendTwoFactorCode } from '../../../pages/user/login/services/post/login/twoFactorAuth';
import { TwoFactorMethod } from '../../../types/twoFactor';
import DigitCodeInput from './DigitCodeInput';

import {
  pageSx,
  cardSx,
  primaryButtonSx,
  resendButtonSx,
  iconContainerSx,
  infoBoxSx,
  errorInfoBoxSx,
  linkTextSx,
  loadingInfoBoxSx,
  successAlertSx,
  errorAlertSx,
  tfaStepLabelSx,
  tfaMethodChipSx,
} from './styles';

type SsoStatus = 'loading' | 'finalizing' | 'tfa' | 'success' | 'error';
type SsoMode = 'create' | 'sync';

/**
 * SSO Bridge Page for "Sign in with RaiseFX" flow.
 *
 * Opened in a new tab by the RFX platform with search params:
 *   /sso?token=<temporaryRfxToken>&createRmfAccount=true            → Mode 1: account creation
 *   /sso?token=<temporaryRfxToken>&emailOnRmf=abc@email.com         → Mode 2: sync/switch (already synced = auto navigate)
 *   /sso?token=<temporaryRfxToken>&emailOnRmf=abc@email.com&isFirstSync=true → Mode 2: first sync
 *
 * Dev mode: /sso?dev=<state> to preview each visual state without a backend.
 */
const SsoPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation('sso');
  const hasStarted = useRef(false);

  const [status, setStatus] = useState<SsoStatus>('loading');
  const [error, setError] = useState<string | null>(null);

  // 2FA state
  const [twoFactorMethods, setTwoFactorMethods] = useState<TwoFactorMethod[]>([]);
  const [twoFactorEmail, setTwoFactorEmail] = useState<string>('');
  const [tfaCode, setTfaCode] = useState('');
  const [tfaDualStep, setTfaDualStep] = useState<1 | 2>(1);
  const [tfaSubmitting, setTfaSubmitting] = useState(false);
  const [tfaError, setTfaError] = useState<string | null>(null);
  const [tfaInfo, setTfaInfo] = useState<string | null>(null);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [isSendingCode, setIsSendingCode] = useState(false);

  // Params
  const token = searchParams.get('token');
  const emailOnRmf = searchParams.get('emailOnRmf');
  const createRmfAccount = searchParams.get('createRmfAccount') === 'true';
  const devMode = searchParams.get('dev');
  const lang = searchParams.get('lang');
  const isFirstSync = devMode
    ? (devMode === 'success' || devMode === 'error-sync')
    : searchParams.get('isFirstSync') === 'true';
  const mode: SsoMode = devMode?.includes('create') ? 'create'
    : createRmfAccount ? 'create'
    : 'sync';

  // Override i18n language if lang param is provided
  useEffect(() => {
    if (lang && i18n.language !== lang) {
      i18n.changeLanguage(lang);
    }
  }, [lang, i18n]);

  // ─── Dev mode ────────────────────────────────────────────────────────────────

  useEffect(() => {
    if (hasStarted.current) return;
    hasStarted.current = true;

    if (devMode) {
      switch (devMode) {
        case 'loading':
          setStatus('loading');
          break;
        case 'loading-create':
        case 'loading-sync':
          setStatus('finalizing');
          break;
        case 'tfa-single':
          setTwoFactorMethods([TwoFactorMethod.APP]);
          setTwoFactorEmail('user@example.com');
          setStatus('tfa');
          break;
        case 'tfa-dual':
          setTwoFactorMethods([TwoFactorMethod.EMAIL, TwoFactorMethod.APP]);
          setTwoFactorEmail('user@example.com');
          setStatus('tfa');
          break;
        case 'success':
          setStatus('success');
          break;
        case 'error-create':
          setError('SSO authentication failed.');
          setStatus('error');
          break;
        case 'error-sync':
          setError('SSO authentication failed.');
          setStatus('error');
          break;
        case 'error-switch':
          setError('SSO authentication failed.');
          setStatus('error');
          break;
        case 'error-cancelled':
          setError('Authentication was cancelled.');
          setStatus('error');
          break;
      }
      return;
    }

    if (!token) {
      setError('No SSO token provided.');
      setStatus('error');
      return;
    }

    performLogin();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Resend cooldown timer
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const id = window.setInterval(() => {
      setResendCooldown((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    return () => window.clearInterval(id);
  }, [resendCooldown]);

  // ─── Login flow ──────────────────────────────────────────────────────────────

  const performLogin = async () => {
    try {
      const response = await loginWithRfx(token!, emailOnRmf, createRmfAccount);

      if (response.data === 'SKIP') {
        window.location.replace('/');
        return;
      }

      if (response.status === 'success' && Array.isArray(response.data)) {
        setTwoFactorMethods(response.data);
        setTwoFactorEmail(emailOnRmf || '');
        setStatus('tfa');
        return;
      }

      await handleLoginSuccess(response);
    } catch (err: any) {
      setError(err?.response?.data?.error || err.message || 'SSO authentication failed.');
      setStatus('error');
    }
  };

  const handleLoginSuccess = async (response: any) => {
    const data = response?.data ?? response;
    const tokens = data?.tokens ?? data;

    tokenStorage.saveTokens(tokens.accessToken, tokens.refreshToken);
    if (lang && mode === 'create') {
      localStorage.setItem('i18nextLng', lang);
    }
    try { trackLogin('rfx'); } catch {}

    if (!isFirstSync && mode !== 'create') {
      window.location.replace('/');
      return;
    }

    // Show the finalizing screen for at least 1.5s before success
    setStatus('finalizing');
    await new Promise((r) => setTimeout(r, 2000));
    setStatus('success');
  };

  // ─── 2FA flow ────────────────────────────────────────────────────────────────

  const requireBothMethods = twoFactorMethods.length > 1;

  const currentTfaMethod: TwoFactorMethod = requireBothMethods
    ? (tfaDualStep === 1 ? TwoFactorMethod.EMAIL : TwoFactorMethod.APP)
    : (twoFactorMethods[0] ?? TwoFactorMethod.EMAIL);

  const isEmailStep = currentTfaMethod === TwoFactorMethod.EMAIL;

  const handleTfaVerify = async () => {
    setTfaError(null);
    setTfaInfo(null);

    if (tfaCode.trim().length !== 6) {
      setTfaError(t('TFA.ENTER_CODE'));
      return;
    }

    setTfaSubmitting(true);

    try {
      const response = await verifyTwoFactorLogin({
        email: twoFactorEmail,
        method: currentTfaMethod,
        code: tfaCode.trim(),
      });

      // Partial success: method validated, more methods required
      if (response.status === 'success' && !response.data) {
        if (requireBothMethods && tfaDualStep === 1) {
          setTfaCode('');
          setTfaError(null);
          setTfaInfo(t('TFA.EMAIL_VERIFIED'));
          setTfaDualStep(2);
        }
        setTfaSubmitting(false);
        return;
      }

      // Full success: all methods validated, tokens received
      if (response.status === 'success' && response.data) {
        await handleLoginSuccess(response);
      }
    } catch (err: any) {
      const errStatus = err?.response?.status ?? err?.status;
      if (errStatus === 429) {
        setTfaError(t('TFA.TOO_MANY_ATTEMPTS'));
      } else if (errStatus === 400) {
        const msg =
          err?.response?.data?.error ||
          err?.response?.data?.message ||
          t('TFA.INVALID_CODE');
        if (msg.toLowerCase().includes('method not found')) {
          setError(msg);
          setStatus('error');
        } else {
          setTfaError(msg);
        }
      } else {
        setError(err?.response?.data?.error || err.message || t('ERROR.VERIFICATION_FAILED'));
        setStatus('error');
      }
    } finally {
      setTfaSubmitting(false);
    }
  };

  const handleResendCode = async () => {
    if (resendCooldown > 0 || isSendingCode) return;
    setTfaError(null);
    setTfaInfo(null);
    setIsSendingCode(true);

    try {
      await sendTwoFactorCode(twoFactorEmail);
      setResendCooldown(60);
      setTfaInfo(t('TFA.NEW_CODE_SENT'));
    } catch {
      setTfaError(t('TFA.RESEND_FAILED'));
    } finally {
      setIsSendingCode(false);
    }
  };

  const handleTfaCancel = () => {
    setTwoFactorMethods([]);
    setStatus('error');
    setError(t('TFA.CANCELLED'));
  };

  // ─── Render ──────────────────────────────────────────────────────────────────

  const renderContent = () => {
    // ─── Loading (waiting for /login response) ──────────────────────────
    if (status === 'loading') {
      return (
        <Card sx={cardSx}>
          <CircularProgress sx={{ color: '#354EEF' }} />
          <Typography variant="body1" sx={{ color: '#CCD4FF' }}>
            {t('LOADING.SIGNING_IN')}
          </Typography>
        </Card>
      );
    }

    // ─── Finalizing (JWT received, showing before success) ────────────────
    if (status === 'finalizing') {
      return (
        <Card sx={cardSx}>
          <CircularProgress sx={{ color: '#354EEF' }} />
          <Typography variant="h5" sx={{ color: '#fff', fontWeight: 700 }}>
            {mode === 'create' ? t('FINALIZING.TITLE_CREATE') : t('FINALIZING.TITLE_SYNC')}
          </Typography>
          <Typography variant="body1" sx={{ color: '#CCD4FF' }}>
            {mode === 'create'
              ? t('FINALIZING.DESCRIPTION_CREATE')
              : t('FINALIZING.DESCRIPTION_SYNC')}
          </Typography>
          <Typography variant="body2" sx={{
            color: 'rgba(255, 255, 255, 0.5)',
            ...(mode === 'create' ? loadingInfoBoxSx as object : {}),
          }}>
            {mode === 'create'
              ? t('FINALIZING.INFO_CREATE')
              : t('FINALIZING.INFO_SYNC')}
          </Typography>
        </Card>
      );
    }

    // ─── 2FA ─────────────────────────────────────────────────────────────
    if (status === 'tfa') {
      return (
        <Card sx={cardSx}>
          {requireBothMethods && (
            <Typography sx={tfaStepLabelSx}>
              {t('TFA.STEP_LABEL', { current: tfaDualStep, total: 2 })}
            </Typography>
          )}

          <Box sx={iconContainerSx}>
            <ShieldIcon sx={{ color: '#CCD4FF', fontSize: 24 }} />
          </Box>

          <Typography variant="h5" sx={{ color: '#fff', fontWeight: 700 }}>
            {t('TFA.TITLE')}
          </Typography>

          <Typography variant="body2" sx={{ color: '#CCD4FF' }}>
            {isFirstSync || mode === 'create'
              ? t('TFA.DESCRIPTION_SYNC')
              : t('TFA.DESCRIPTION_ACCESS')}
          </Typography>

          <Typography sx={tfaMethodChipSx}>
            {isEmailStep ? t('TFA.METHOD_EMAIL') : t('TFA.METHOD_APP')}
          </Typography>

          {tfaInfo && <Alert severity="success" sx={successAlertSx}>{tfaInfo}</Alert>}
          {tfaError && <Alert severity="error" sx={errorAlertSx}>{tfaError}</Alert>}

          <DigitCodeInput
            value={tfaCode}
            onChange={setTfaCode}
            disabled={tfaSubmitting}
            autoFocus
          />

          {isEmailStep && (
            <Button
              variant="text"
              size="small"
              onClick={handleResendCode}
              disabled={isSendingCode || resendCooldown > 0}
              sx={resendButtonSx}
            >
              {isSendingCode ? (
                <CircularProgress size={14} sx={{ color: '#354EEF' }} />
              ) : resendCooldown > 0 ? (
                t('TFA.RESEND_CODE_COUNTDOWN', { seconds: resendCooldown })
              ) : (
                t('TFA.RESEND_CODE')
              )}
            </Button>
          )}

          <Button
            variant="contained"
            onClick={handleTfaVerify}
            disabled={tfaSubmitting || tfaCode.length < 6}
            sx={primaryButtonSx}
          >
            {tfaSubmitting ? (
              <>
                <CircularProgress size={18} sx={{ mr: 1, color: '#fff' }} />
                {t('TFA.VERIFYING')}
              </>
            ) : (
              t('TFA.VERIFY')
            )}
          </Button>
        </Card>
      );
    }

    // ─── Success ─────────────────────────────────────────────────────────
    if (status === 'success') {
      return (
        <Card sx={cardSx}>
          <Box sx={iconContainerSx}>
            <CheckCircleOutlineIcon sx={{ color: '#4caf50', fontSize: 24 }} />
          </Box>
          <Typography variant="h5" sx={{ color: '#fff', fontWeight: 700 }}>
            {t('SUCCESS.TITLE')}
          </Typography>
          <Typography variant="body1" sx={{ color: '#CCD4FF' }}>
            {t('SUCCESS.DESCRIPTION')}
          </Typography>
          <Box sx={infoBoxSx}>
            <Typography variant="body2" sx={{ color: '#4caf50', textAlign: 'center' }}>
              {t('SUCCESS.INFO')}
            </Typography>
          </Box>
          <Button
            variant="contained"
            fullWidth
            onClick={() => window.location.replace('/')}
            sx={primaryButtonSx}
          >
            {t('SUCCESS.ACCESS_PLATFORM')}
          </Button>
        </Card>
      );
    }

    // ─── Error ───────────────────────────────────────────────────────────
    return (
      <Card sx={cardSx}>
        <Box sx={iconContainerSx}>
          <ErrorOutlineIcon sx={{ color: '#f44336', fontSize: 24 }} />
        </Box>
        <Typography variant="h5" sx={{ color: '#fff', fontWeight: 700 }}>
          {t('ERROR.TITLE')}
        </Typography>
        <Typography variant="body1" sx={{ color: '#CCD4FF' }}>
          {t('ERROR.DESCRIPTION')}
        </Typography>
        <Box sx={errorInfoBoxSx}>
          <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.5)', textAlign: 'center' }}>
            {t('ERROR.CONTACT')}{' '}
              <Typography component="span" variant="body2" sx={linkTextSx}>{t('ERROR.SUPPORT_EMAIL')}</Typography>
          </Typography>
        </Box>
        <Button
          variant="contained"
          fullWidth
          onClick={() => { window.close(); navigate('/login', { replace: true }); }}
          sx={primaryButtonSx}
        >
          {t('ERROR.CLOSE')}
        </Button>
      </Card>
    );
  };

  return (
    <Box sx={pageSx}>
      {renderContent()}
    </Box>
  );
};

export default SsoPage;
